import { createContext, useContext, useState, useEffect } from 'react'
import authService from '../services/authService'

const AuthContext = createContext(null)

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    checkAuth()
  }, [])

  const checkAuth = async () => {
    if (authService.isAuthenticated()) {
      try {
        const userData = await authService.getCurrentUser()
        setUser(userData)
      } catch (err) {
        console.error('Auth check failed:', err)
        authService.logout()
      }
    }
    setLoading(false)
  }

  const register = async (data) => {
    try {
      setError(null)
      const result = await authService.register(data)
      return result
    } catch (err) {
      setError(err.response?.data?.message || 'Registration failed')
      throw err
    }
  }

  const login = async (email, password) => {
    try {
      setError(null)
      const userData = await authService.login(email, password)
      setUser(userData)
      return userData
    } catch (err) {
      setError(err.response?.data?.message || 'Login failed')
      throw err
    }
  }

  const logout = () => {
    authService.logout()
    setUser(null)
  }

  const googleLogin = async (credential) => {
    try {
      setError(null)
      const userData = await authService.googleLogin(credential)
      setUser(userData)
      return userData
    } catch (err) {
      setError(err.response?.data?.message || 'Google login failed')
      throw err
    }
  }

  const facebookLogin = async (accessToken) => {
    try {
      setError(null)
      const userData = await authService.facebookLogin(accessToken)
      setUser(userData)
      return userData
    } catch (err) {
      setError(err.response?.data?.message || 'Facebook login failed')
      throw err
    }
  }

  const twitterLogin = async (code) => {
    try {
      setError(null)
      const userData = await authService.twitterLogin(code)
      setUser(userData)
      return userData
    } catch (err) {
      setError(err.response?.data?.message || 'Twitter login failed')
      throw err
    }
  }

  const sendPhoneCode = async (phoneNumber) => {
    try {
      setError(null)
      const result = await authService.sendPhoneCode(phoneNumber)
      return result
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to send verification code')
      throw err
    }
  }

  const verifyPhoneCode = async (phoneNumber, code, name = null) => {
    try {
      setError(null)
      const { user: userData, isNewUser } = await authService.verifyPhoneCode(phoneNumber, code, name)
      setUser(userData)
      return { user: userData, isNewUser }
    } catch (err) {
      setError(err.response?.data?.message || 'Phone verification failed')
      throw err
    }
  }

  const signalLogin = async (phoneNumber, code, name = null) => {
    return verifyPhoneCode(phoneNumber, code, name)
  }

  const refreshUser = async () => {
    // Refresh current user data from server
    try {
      const userData = await authService.getCurrentUser()
      setUser(userData)
      return userData
    } catch (err) {
      console.error('Failed to refresh user:', err)
      throw err
    }
  }

  const value = {
    user,
    loading,
    error,
    register,
    login,
    logout,
    googleLogin,
    facebookLogin,
    twitterLogin,
    sendPhoneCode,
    verifyPhoneCode,
    signalLogin,
    checkAuth,
    refreshUser,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export default AuthContext
